import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";

const schema = z.object({
  hub_url: z
    .string()
    .trim()
    .url("Enter the full Cornerstone Hub URL, e.g. https://hub.cornerstonepms.com")
    .max(512),
  api_key: z.string().trim().min(8, "API key must be at least 8 characters").max(512),
});

export type CornerstoneCredentialValues = z.infer<typeof schema>;

interface Props {
  busy: boolean;
  reconnecting: boolean;
  onSubmit: (v: CornerstoneCredentialValues) => void;
  onCancel?: () => void;
}

/**
 * Cornerstone Hub credentials form.
 * Write-only: VetGuard validates the API key with Cornerstone Hub before
 * storing it encrypted; the key is never shown again.
 */
export function CornerstoneCredentialsForm({ busy, reconnecting, onSubmit, onCancel }: Props) {
  const [showKey, setShowKey] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<CornerstoneCredentialValues>({
    resolver: zodResolver(schema),
    defaultValues: { hub_url: "https://hub.cornerstonepms.com", api_key: "" },
  });

  return (
    <form
      className="card"
      onSubmit={handleSubmit(onSubmit)}
      autoComplete="off"
      aria-labelledby="cs-creds-title"
    >
      <h2 id="cs-creds-title">
        {reconnecting ? "Update Cornerstone credentials" : "Connect Cornerstone"}
      </h2>

      <div className="callout" style={{ marginBottom: 16 }}>
        <strong>How to get your API key</strong>
        <ol style={{ margin: "8px 0 0 16px", padding: 0, lineHeight: 1.7 }}>
          <li>Log in to your Cornerstone Hub admin portal (IDEXX).</li>
          <li>Navigate to <strong>Settings → API Access</strong>.</li>
          <li>Generate a new API key with <em>Read: Visits, Invoices, SOAP Notes</em> scopes.</li>
          <li>Copy the key and paste it below — it is only shown once in Cornerstone.</li>
        </ol>
        <p style={{ margin: "8px 0 0" }}>
          VetGuard validates the key against Cornerstone Hub before saving.
          It is stored encrypted and never shown again.
        </p>
      </div>

      <div className="field">
        <label htmlFor="cs-hub-url">Cornerstone Hub URL</label>
        <input
          id="cs-hub-url"
          type="url"
          spellCheck={false}
          placeholder="https://hub.cornerstonepms.com"
          {...register("hub_url")}
        />
        {errors.hub_url && <span className="field-error">{errors.hub_url.message}</span>}
      </div>

      <div className="field">
        <label htmlFor="cs-api-key">API key</label>
        <div className="row" style={{ gap: 8 }}>
          <input
            id="cs-api-key"
            type={showKey ? "text" : "password"}
            autoComplete="new-password"
            spellCheck={false}
            className="mono grow"
            placeholder="Paste your Cornerstone Hub API key"
            {...register("api_key")}
          />
          <button
            type="button"
            className="btn btn-sm"
            onClick={() => setShowKey((s) => !s)}
            aria-pressed={showKey}
            aria-controls="cs-api-key"
          >
            {showKey ? "Hide" : "Show"}
          </button>
        </div>
        {errors.api_key && <span className="field-error">{errors.api_key.message}</span>}
      </div>

      <div className="row">
        <button type="submit" className="btn btn-primary" disabled={busy} id="cs-connect-btn">
          {busy
            ? "Validating with Cornerstone…"
            : reconnecting
            ? "Save credentials"
            : "Connect Cornerstone"}
        </button>
        {onCancel && (
          <button type="button" className="btn" onClick={onCancel} disabled={busy}>
            Cancel
          </button>
        )}
      </div>
    </form>
  );
}
